import { deviceView } from "../builder";
import type { LibraryTemplate } from "../library";
import { type DeviceSpec, PHONE, widthAt } from "./mockups";
import {
  BLACK,
  captions,
  cornerLogo,
  device,
  during,
  ellipse,
  enter,
  icon,
  logo,
  ON_VIDEO,
  parts,
  photo,
  pill,
  REEL_CAPTIONS,
  rect,
  stack,
  t,
  text,
} from "./shorthands";

/**
 * Reels with a speaker and a phone (docs/19-mockups.md): someone talks to
 * the camera and shows something in an app. The speaker's video is the main
 * track (black page) or goes into the "Sprecher" placeholder; the screen
 * recording goes into the phone. The editor's "Sprecher & App" card puts
 * both in place. Clips always lie below the page's elements, so nothing
 * that is drawn before a phone or the speaker slot may overlap it.
 */

/** Name of the placeholder the speaker's video goes into (`SPEAKER_SLOT` in the editor). */
const SPEAKER = "Sprecher";

const FRONT: DeviceSpec = { device: PHONE, tilt: deviceView(PHONE, "front") };
const SHADOW = { color: "#00000080", y: 36, blur: 80 };
const TAGS = ["mockup", "sprecher", "talking head", "app", "bildschirmaufnahme", "handy"];

export const SPEAKER_REELS: LibraryTemplate[] = [
  {
    id: "reel-sprecher-handy-daneben",
    title: "Reel: Sprecher mit Handy daneben",
    description:
      "Du sprichst, links daneben läuft die Bildschirmaufnahme im Handy – stell dich rechts ins Bild",
    presetId: "reel",
    topic: "wissen",
    style: "klar",
    tags: [...TAGS, "erklären", "zeigen", "tutorial"],
    pages: [
      {
        background: BLACK,
        nodes: [
          ...pill(60, 250, 330, 64, "IN DER APP", "accent", 28, enter("slide-down")),
          cornerLogo(),
          device({
            ...FRONT,
            tilt: { ...FRONT.tilt, z: -4 },
            x: 60,
            y: 340,
            h: 740,
            shadow: SHADOW,
            name: "iPhone",
            ...enter("fly-right", { duration: 700 }),
          }),
          captions(REEL_CAPTIONS),
        ],
      },
    ],
  },
  {
    id: "reel-sprecher-handy-unten",
    title: "Reel: Sprecher, Titel oben, Handy unten",
    description: "Titelkarte oben, du in der Mitte, das Handy unten links und Untertitel daneben",
    presetId: "reel",
    topic: "wissen",
    style: "verspielt",
    tags: [...TAGS, "app-tipp", "tipp", "erklären"],
    pages: [
      {
        background: BLACK,
        nodes: [
          rect({
            x: 60,
            y: 240,
            w: 960,
            h: 190,
            fill: "background/92",
            radius: "lg",
            name: "Titel-Karte",
            ...enter("slide-down"),
          }),
          stack({
            x: 100,
            y: 335,
            w: 640,
            anchor: "center",
            gap: 6,
            items: [
              t("APP-TIPP", "subline", 28, "primary", enter("slide-down")),
              t("So findest du deine Rechnungen", "heading-2", 52, "text", enter("slide-down")),
            ],
          }),
          logo({
            x: 770,
            y: 262,
            w: 210,
            h: 52,
            on: "background",
            align: "right",
            ...enter("slide-down"),
          }),
          device({
            ...FRONT,
            tilt: { ...FRONT.tilt, z: -5 },
            x: 60,
            y: 960,
            h: 620,
            shadow: SHADOW,
            name: "iPhone",
            ...enter("fly-up", { duration: 800 }),
          }),
          captions({ x: 400, y: 1080, w: 520, h: 340 }),
        ],
      },
    ],
  },
  {
    id: "reel-sprecher-handy-stichwort",
    title: "Reel: Sprecher, dann Handy auf Stichwort",
    description:
      "Erst du mit Hook, nach 3 Sekunden fliegt das Handy mit der Bildschirmaufnahme herein",
    presetId: "reel",
    topic: "produkt",
    style: "nacht",
    tags: [...TAGS, "hook", "demo", "ich zeig's dir"],
    previewTime: 4500,
    pages: [
      {
        background: BLACK,
        nodes: [
          rect({
            x: 60,
            y: 300,
            w: 960,
            h: 230,
            fill: "primary",
            radius: "md",
            rotation: -2,
            name: "Hook",
            ...during(0, 3000, "pop", { duration: 400 }),
          }),
          text({
            text: "Diese Funktion kennt kaum jemand",
            style: "heading-1",
            size: 76,
            color: "on:primary",
            x: 100,
            y: 300,
            w: 880,
            h: 230,
            align: "center",
            rotation: -2,
            ...during(0, 3000, "pop", { duration: 400 }),
          }),
          ...pill(365, 250, 350, 64, "ICH ZEIG’S DIR", "accent", 28, {
            ...during(3000, 20000, "slide-down", { out: null }),
          }),
          device({
            ...FRONT,
            x: (1080 - widthAt(FRONT, 760)) / 2,
            y: 335,
            h: 760,
            shadow: SHADOW,
            name: "iPhone",
            ...during(3000, 20000, "fly-up", { duration: 700, out: null }),
          }),
          captions(REEL_CAPTIONS),
        ],
      },
    ],
  },
  {
    id: "reel-sprecher-kreis-handy",
    title: "Reel: Großes Handy, Sprecher im Kreis",
    description:
      "App-Demo auf Markenfarbe: großes Handy rechts, du im Kreis links, Untertitel darüber",
    presetId: "reel",
    topic: "produkt",
    style: "verspielt",
    tags: [...TAGS, "bild im bild", "kreis", "demo", "app-demo"],
    pages: [
      {
        background: "primary",
        nodes: [
          ellipse({ x: -160, y: -200, w: 560, h: 560, fill: "on:primary/10", name: "Kreis" }),
          stack({
            x: 60,
            y: 250,
            w: 960,
            gap: 10,
            items: [
              t("APP-DEMO", "subline", 32, "on:primary/80", enter("fade")),
              t("So planst du deine Woche", "heading-1", 84, "on:primary", enter("slide-up")),
            ],
          }),
          device({
            ...FRONT,
            x: 1020 - widthAt(FRONT, 1120),
            y: 580,
            h: 1120,
            shadow: SHADOW,
            name: "iPhone",
            ...enter("fly-left", { duration: 800 }),
          }),
          captions({ x: 60, y: 570, w: 390, h: 300 }),
          photo({
            x: 60,
            y: 940,
            w: 390,
            h: 390,
            ellipse: true,
            fill: "on:primary/20",
            name: SPEAKER,
            ...enter("pop", { duration: 700 }),
          }),
          ellipse({
            x: 48,
            y: 928,
            w: 414,
            h: 414,
            stroke: { color: "accent", width: 10 },
            name: "Ring",
            ...enter("pop", { duration: 700 }),
          }),
          ...pill(70, 1360, 370, 70, "Lena · Gründerin", "accent", 28, {
            ...enter("slide-up", { duration: 900 }),
          }),
        ],
      },
    ],
  },
  {
    id: "reel-sprecher-geteilt",
    title: "Reel: Handy oben, Sprecher unten",
    description: "Geteilter Bildschirm: oben die App im Handy, unten du – mit Untertiteln",
    presetId: "reel",
    topic: "wissen",
    style: "nacht",
    tags: [...TAGS, "split screen", "geteilt", "reaktion", "tutorial"],
    pages: [
      {
        background: "secondary",
        nodes: [
          ...pill(60, 250, 260, 64, "LIVE-DEMO", "accent", 28, enter("slide-right")),
          logo({ x: 60, y: 340, w: 220, h: 60, on: "secondary" }),
          device({
            ...FRONT,
            x: (1080 - widthAt(FRONT, 780)) / 2,
            y: 250,
            h: 780,
            shadow: SHADOW,
            name: "iPhone",
            ...enter("fly-down", { duration: 700 }),
          }),
          photo({ x: 0, y: 1070, w: 1080, h: 850, fill: "on:secondary/15", name: SPEAKER }),
          captions({ x: 76, y: 1150, w: 928, h: 280 }),
        ],
      },
    ],
  },
  {
    id: "reel-sprecher-app-review",
    title: "Reel: App-Review mit Bewertung",
    description: "Handy links, Sterne-Bewertung oben, dann Pro und Contra nacheinander – dazu du",
    presetId: "reel",
    topic: "produkt",
    style: "laut",
    tags: [...TAGS, "review", "test", "bewertung", "sterne", "pro contra", "meinung"],
    previewTime: 4500,
    pages: [
      {
        background: BLACK,
        nodes: [
          device({
            ...FRONT,
            tilt: { ...FRONT.tilt, z: -4 },
            x: 50,
            y: 330,
            h: 700,
            shadow: SHADOW,
            name: "iPhone",
            ...enter("fly-right", { duration: 700 }),
          }),
          rect({
            x: 450,
            y: 250,
            w: 570,
            h: 160,
            fill: "background/92",
            radius: "lg",
            name: "Bewertung",
            ...enter("slide-left"),
          }),
          text({
            text: "MEINE BEWERTUNG",
            style: "subline",
            size: 26,
            color: "text",
            x: 480,
            y: 268,
            w: 510,
            ...enter("slide-left"),
          }),
          ...[0, 1, 2, 3, 4].map((index) =>
            icon({
              icon: "star",
              x: 480 + index * 76,
              y: 318,
              w: 64,
              h: 64,
              color: index < 4 ? "accent" : "text/25",
              name: `Stern ${index + 1}`,
              ...enter("pop", { duration: 500 + index * 150 }),
            }),
          ),
          ...(
            [
              { label: "PRO", text: "In zwei Minuten eingerichtet", fill: "primary", from: 2500 },
              { label: "CONTRA", text: "Noch kein Dunkelmodus", fill: "accent", from: 7500 },
            ] as const
          ).flatMap((point) => {
            const motion = during(point.from, point.from + 5000, "slide-left", { duration: 450 });
            return [
              ...pill(450, 870, 200, 60, point.label, point.fill, 26, motion),
              text({
                text: point.text,
                style: "heading-2",
                size: 48,
                x: 450,
                y: 945,
                w: 570,
                ...ON_VIDEO,
                ...motion,
              }),
            ];
          }),
          captions(REEL_CAPTIONS),
        ],
      },
    ],
  },
  {
    id: "reel-sprecher-3-funktionen",
    title: "Reel: Sprecher zeigt 3 Funktionen",
    description:
      "Handy rechts, links erscheinen Funktion 1 bis 3 nacheinander – dazu du und Untertitel",
    presetId: "reel",
    topic: "produkt",
    style: "warm",
    tags: [...TAGS, "funktionen", "features", "top 3", "liste"],
    previewTime: 2000,
    pages: [
      {
        background: BLACK,
        nodes: [
          ...pill(60, 250, 360, 64, "3 FUNKTIONEN", "primary", 28, enter("slide-down")),
          device({
            ...FRONT,
            tilt: { ...FRONT.tilt, z: 4 },
            x: 1030 - widthAt(FRONT, 740),
            y: 330,
            h: 740,
            shadow: SHADOW,
            name: "iPhone",
            ...enter("fly-left", { duration: 700 }),
          }),
          ...parts(3, 1000, 4000).flatMap(({ index, start, end }) => {
            const features = ["Termine teilen", "Erinnerung per Push", "Alles offline"];
            const motion = during(start, end, "slide-right", { duration: 450 });
            return [
              ellipse({
                x: 60,
                y: 360,
                w: 110,
                h: 110,
                fill: "accent",
                name: `Nummer ${index + 1}`,
                ...motion,
              }),
              text({
                text: String(index + 1),
                style: "heading-1",
                size: 64,
                color: "on:accent",
                x: 60,
                y: 360,
                w: 110,
                h: 110,
                align: "center",
                ...motion,
              }),
              text({
                text: features[index]!,
                style: "heading-2",
                size: 54,
                x: 60,
                y: 490,
                w: 540,
                ...ON_VIDEO,
                ...motion,
              }),
            ];
          }),
          captions(REEL_CAPTIONS),
        ],
      },
    ],
  },
];
